import { useEffect, useState } from 'react'

import { Typography } from '@mui/material'

import type { AuthenticatorVerifyResultSchema, TwoFactorAuthDetailsSchema } from '@/api/types'

import { api } from '@/api'
import { TwoFactorAuthType } from '@/api/types'
import { AuthenticatorEnrollment } from '@/components/auth/authenticator-enrollment'
import { MfaVerification } from '@/components/auth/mfa-verification'
import { queryClient } from '@/lib/query-client'
import { authService } from '@/services/auth-service'
import { useTwoFactorEnrollmentState } from '@/states/two-factor-enrollment-state'

import { StepUpEnrollmentRequired } from './step-up-enrollment-required'

interface Props {
  open: boolean
  onVerified: VoidFunction
  onDismiss: VoidFunction
}

// Step-up takes a confirmed phone or an authenticator, whatever the company asks of a NEW enrolment.
function canProveStepUp(details: TwoFactorAuthDetailsSchema) {
  return details.hasAuthenticator || (details.phoneNumberConfirmed && !!details.phoneNumber)
}

// They prove the factor they already hold; email never approves a sensitive action, so it is never one of these.
function methodToProve(details: TwoFactorAuthDetailsSchema) {
  if (details.hasAuthenticator) return TwoFactorAuthType.Authenticator
  return details.authType === TwoFactorAuthType.Call ? TwoFactorAuthType.Call : TwoFactorAuthType.Sms
}

export function StepUpAuthContent({ open, onVerified, onDismiss }: Props) {
  const [enrollingAuthenticator, setEnrollingAuthenticator] = useState(false)

  const openEnrollment = useTwoFactorEnrollmentState((state) => state.openEnrollment)

  const details = api.useGetTwoFactorAuthDetails({ query: { enabled: open } }).data?.data

  const stepUp = api.useVerifyTwoFactorStepUp({ mutation: { onSuccess: onVerified } })

  // A first enrolment proves possession, so the server marks the session and hands back a fresh token.
  const finishEnrollment = (token?: string | null, jwtGuid?: string | null) => {
    authService.saveToken(token)
    authService.saveJwtGuid(jwtGuid)
    void queryClient.invalidateQueries({ queryKey: api.getGetTwoFactorAuthDetailsQueryKey() })
    onVerified()
  }

  const enrollPhone = api.useVerifyTwoFactorAuthCode({
    mutation: { onSuccess: (response) => finishEnrollment(response.data.token, response.data.jwtGuid) },
  })

  const enrollAuthenticator = (result: AuthenticatorVerifyResultSchema) =>
    finishEnrollment(result.authorization?.token, result.authorization?.jwtGuid)

  const setUpFirstMethod = () => {
    onDismiss()
    openEnrollment()
  }

  useEffect(() => {
    if (open) setEnrollingAuthenticator(false)
  }, [open])

  if (!details) return null

  if (details.authenticatorUnavailable) {
    return (
      <Typography variant="body2">
        Your authenticator app setup cannot be read. Please contact support to restore two-step verification.
      </Typography>
    )
  }

  if (enrollingAuthenticator) return <AuthenticatorEnrollment onEnrolled={enrollAuthenticator} />

  const allowedMethods = details.allowedMethods ?? []

  if (canProveStepUp(details)) {
    return (
      <MfaVerification
        viewType="step-up"
        allowedMethods={allowedMethods}
        hasMfaMethod
        method={methodToProve(details)}
        onVerify={(data) => stepUp.mutate({ data: { code: data.code } })}
      />
    )
  }

  // No factor at all: a first enrolment belongs in the two-step setup, not behind a dialog that cannot settle.
  if (!details.hasAnyFactor) return <StepUpEnrollmentRequired onSetUp={setUpFirstMethod} />

  // Email signed them in but never approves a sensitive action, so an email user enrols a real method here.
  return (
    <MfaVerification
      viewType="enroll"
      allowedMethods={allowedMethods}
      hasMfaMethod={false}
      onVerify={(data) => enrollPhone.mutate({ data: { code: data.code } })}
      onEnrollAuthenticator={() => setEnrollingAuthenticator(true)}
    />
  )
}
